/**
 * Basic Event Emitter implementation for wiring together analytics events.
 */

'use strict';

/**
 * Context-specific helper function meant to determine if a callback is really a callback.
 *
 * @param {*} maybeFunction
 *
 * @returns {boolean}
 */
let isFunction = function( maybeFunction ) {
	return typeof maybeFunction === 'function' ||false;
};

export default class EventBus {

	/**
	 * Build the EventEmitter object
	 */
	constructor() {
		this.listeners = new Map();
		this.observers = {}
		this.Observer = Observer
	}

	/**
	 * Add a named event listener.
	 *
	 * @param {string}   label
	 * @param {function} callback
	 */
	addListener( label, callback ) {
		this.listeners.has( label ) || this.listeners.set( label, [] );
		this.listeners.get( label ).push( callback );
	}

	/**
	 * Remove a specific listener from the collection.
	 *
	 * @param {string}   label
	 * @param {function} callback
	 *
	 * @returns {boolean}
	 */
	removeListener( label, callback ) {
		let listeners = this.listeners.get( label ),
			index;

		if ( listeners && listeners.length ) {
			index = listeners.reduce( ( i, listener, index ) => {
				return ( isFunction( listener ) && listener === callback ) ? i = index : i;
			}, -1 );

			if ( index > -1 ) {
				listeners.splice( index, 1 );
				this.listeners.set( label, listeners );
				return true;
			}
		}

		return false;
	}

	/**
	 * Invoke the subscribed callbacks for a specific event, passing optional arguments along with it.
	 *
	 * @param {string} label
	 * @param {*}      args
	 *
	 * @returns {boolean}
	 */
	emit( label, ...args ) {
		let listeners = this.listeners.get(label);

		if (listeners && listeners.length) {
			listeners.forEach((listener) => {
				listener(...args);
			});
			return true;
		}
		return false;
	}

	/**
	 * Direct alias of addListener for convenience
	 *
	 * @param {string}   label
	 * @param {function} callback
	 */
	on( label, callback ) {
		this.addListener( label, callback );
	}

	/**
	 * Direct alias of removeListener for convenience
	 *
	 * @param {string}   label
	 * @param {function} callback
	 *
	 * @returns {boolean}
	 */
	off( label, callback ) {
		return this.removeListener( label, callback );
	}

	/**
	 * Direct alias of trigger for convenience
	 *
	 * @param {string} label
	 * @param {*}      args
	 *
	 * @return {boolean}
	 */
	trigger( label, ...args ) {
		return this.emit( label, ...args );
	}

	/**
	 * Helper function to add an observer and check existing key.
	 *
	 * @param key {String}
	 * @param ObserverClass {Class}
	 * @returns {*}
	 */
	addObserver( key, ObserverClass ) {
		if ( ! this.observers[key] ) {
			this.observers[key] = new ObserverClass()
		}

		return this.observers[key]
	}
}

/**
 * Helper interface for EventBus.
 */
class Observer {
	/**
	 * Build a new observer and automatically wire it to specific events.
	 *
	 * @param {string}   event
	 * @param {function} callback
	 */
	constructor( event, callback ) {
		this.event = event;
	}

	/**
	 * Define the callback for this observable
	 *
	 * @param {function} callback
	 */
	setCallback( callback ) {
		this.callback = callback;
		this.bound = true;
		window.EventBus.addListener( this.event, this.callback );
	}

	/**
	 * Basic event logging for debugging purposes
	 *
	 * @param {*} data
	 */
	log( data ) {
		if ( undefined !== console.log && this.bound ) {
			// silence logger
			// console.log( 'EventBus | ' + this.event + ' | ' + JSON.stringify( data ) );
		}
	}

	/**
	 * Stop listening to events entirely
	 */
	unbind() {
		window.EventBus.removeListener( this.event, this.callback );
		this.bound = false;
	}
}

/**
 * Initialize global.
 * @type {EventBus}
 */
window.EventBus = new EventBus()
